// План погашения долгов: помесячная симуляция стратегий «лавина» и «снежный ком».
// Считает код, а не модель, — цифры должны сходиться до сума.

export interface PayoffDebt {
  id: string;
  name: string;
  /** Остаток в сумах. */
  balance: number;
  /** Процентов годовых. */
  annualRate: number;
  /** Обязательный платёж в месяц (по графику), сумы. */
  minPayment: number;
}

/** avalanche — сначала самый дорогой долг; snowball — сначала самый маленький. */
export type PayoffStrategy = "avalanche" | "snowball";

export interface PayoffResult {
  /** Через сколько месяцев закроются все долги. */
  months: number;
  totalInterest: number;
  /** В каком порядке и на каком месяце закрывается каждый долг. */
  order: { id: string; name: string; month: number }[];
  /** false — при таких платежах долги не закрываются (платёж меньше процентов). */
  finishes: boolean;
}

export interface PayoffOptions {
  strategy: PayoffStrategy;
  /** Сколько сверх обязательных платежей семья готова отдавать в месяц. */
  extra: number;
  /** Освободившийся платёж закрытого долга идёт на следующий (суть обеих стратегий). */
  rollover?: boolean;
  maxMonths?: number;
}

export function simulatePayoff(debts: PayoffDebt[], opts: PayoffOptions): PayoffResult {
  const rollover = opts.rollover ?? true;
  const maxMonths = opts.maxMonths ?? 600;
  const state = debts.filter((d) => d.balance > 0).map((d) => ({ ...d }));
  const order: PayoffResult["order"] = [];
  let totalInterest = 0;
  let month = 0;

  const priority = (a: (typeof state)[number], b: (typeof state)[number]) =>
    opts.strategy === "avalanche" ? b.annualRate - a.annualRate || a.balance - b.balance : a.balance - b.balance || b.annualRate - a.annualRate;

  while (state.some((d) => d.balance > 0.5) && month < maxMonths) {
    month++;
    const active = state.filter((d) => d.balance > 0.5);
    for (const d of active) {
      const interest = (d.balance * d.annualRate) / 1200;
      d.balance += interest;
      totalInterest += interest;
    }
    // Обязательные платежи.
    let budget = opts.extra + (rollover ? state.reduce((s, d) => s + d.minPayment, 0) : active.reduce((s, d) => s + d.minPayment, 0));
    for (const d of active) {
      const pay = Math.min(d.minPayment, d.balance);
      d.balance -= pay;
      budget -= pay;
    }
    // Остаток бюджета — на приоритетный долг, затем на следующий.
    if (!rollover) budget = Math.min(budget, opts.extra);
    for (const d of [...active].sort(priority)) {
      if (budget <= 0) break;
      const pay = Math.min(budget, d.balance);
      d.balance -= pay;
      budget -= pay;
    }
    for (const d of active) {
      if (d.balance <= 0.5) {
        d.balance = 0;
        order.push({ id: d.id, name: d.name, month });
      }
    }
  }
  const finishes = state.every((d) => d.balance <= 0.5);
  return { months: month, totalInterest: Math.round(totalInterest), order, finishes };
}

export interface PayoffPlan {
  /** Только обязательные платежи, без доплат. */
  baseline: PayoffResult;
  avalanche: PayoffResult;
  snowball: PayoffResult;
  /**
   * Что советуем: «снежный ком», только если он закрывает первый долг раньше, а переплачивает немного
   * (< 3% и < 100 тыс.) — ради мотивации; иначе «лавина».
   */
  recommended: PayoffStrategy;
}

export function payoffPlan(debts: PayoffDebt[], extra: number): PayoffPlan {
  const baseline = simulatePayoff(debts, { strategy: "avalanche", extra: 0, rollover: false });
  const avalanche = simulatePayoff(debts, { strategy: "avalanche", extra });
  const snowball = simulatePayoff(debts, { strategy: "snowball", extra });
  const diff = snowball.totalInterest - avalanche.totalInterest;
  const firstSooner = (snowball.order[0]?.month ?? Infinity) < (avalanche.order[0]?.month ?? Infinity);
  const recommended = firstSooner && diff < Math.max(100_000, avalanche.totalInterest * 0.03) ? "snowball" : "avalanche";
  return { baseline, avalanche, snowball, recommended };
}
